import { clsx } from 'clsx'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { useState } from 'react'

interface Props {
  audienceId: string
  hasAudience: boolean
  onTestPassed?: () => void
}

type TestState = 'idle' | 'testing' | 'success' | 'error'

export function TestConnection({
  audienceId,
  hasAudience,
  onTestPassed,
}: Props): React.JSX.Element {
  const [state, setState] = useState<TestState>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  async function handleTest(): Promise<void> {
    if (!audienceId) return
    setState('testing')
    setErrorMessage('')

    const testEmail = `test-${Date.now()}@verify.one-email.com`
    // Use the direct API URL for the public subscribe endpoint (not the proxied /api path)
    // because the proxy may not forward public endpoints correctly
    const apiUrl = 'https://api.one-email.com'

    try {
      const response = await fetch(`${apiUrl}/v1/subscribe/${audienceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: testEmail }),
      })

      if (response.ok) {
        setState('success')
        onTestPassed?.()
      } else {
        const body = (await response.json().catch(() => null)) as Record<string, unknown> | null
        const err = (body?.error ?? body) as Record<string, unknown> | null
        setState('error')
        setErrorMessage((err?.message as string) ?? `Request failed with status ${response.status}`)
      }
    } catch {
      setState('error')
      setErrorMessage('Could not reach the subscribe endpoint. Check your setup.')
    }
  }

  if (!hasAudience) {
    return (
      <p className="text-sm text-text-tertiary">Create an audience first to test the connection.</p>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary">
        Send a test subscription request to verify the full pipeline works.
      </p>

      {state === 'success' && (
        <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-green-50 px-4 py-3">
          <CheckCircle2 className="h-5 w-5 text-success" />
          <span className="text-sm font-medium text-green-800">
            Your subscribe endpoint is working!
          </span>
        </div>
      )}

      {state === 'error' && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-red-50 px-4 py-3">
          <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
          <div>
            <p className="text-sm font-medium text-red-800">Failed — check your setup</p>
            {errorMessage && <p className="mt-1 text-xs text-red-600">{errorMessage}</p>}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleTest}
        disabled={state === 'testing' || !audienceId}
        className={clsx(
          'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
          state === 'success'
            ? 'border border-surface-3 bg-surface-0 text-text-primary hover:bg-surface-1'
            : 'bg-brand-500 text-white hover:bg-brand-600',
          'disabled:opacity-50',
        )}
      >
        {state === 'testing' && <Loader2 className="h-4 w-4 animate-spin" />}
        {state === 'success' ? 'Test Again' : 'Test Connection'}
      </button>
    </div>
  )
}
